/**
 * Hotspot data extraction — filters projects and communities with GPS
 * coordinates, deduplicates by location, and maps to globe marker data.
 */

import { resolveEntityCoords } from "./communityGeoLookup";
import { isWorkshop } from "../../../Data/Community/workshop";

/** What kind of entity a hotspot represents — drives color/animation in the globe. */
export type HotspotEntityKind = "project" | "community";

export interface HotspotData {
  id: string;
  name: string;
  lat: number;
  lon: number;
  color: number;
  /** Discriminator for animation branching in DiscoverGlobe. */
  entityKind: HotspotEntityKind;
}

/** Minimum angular distance (degrees) between markers on the globe (~5° ≈ 550 km). */
const MIN_ANGULAR_DISTANCE = 5;

/**
 * Filter a combined hotspot list so no two markers are within
 * MIN_ANGULAR_DISTANCE degrees of each other.  First-in wins,
 * so callers should order the array by priority before calling.
 */
export function dedupeByProximity(hotspots: HotspotData[]): HotspotData[] {
  const kept: HotspotData[] = [];
  for (const hs of hotspots) {
    const tooClose = kept.some((existing) => {
      const dLat = hs.lat - existing.lat;
      const dLon = hs.lon - existing.lon;
      return Math.sqrt(dLat * dLat + dLon * dLon) < MIN_ANGULAR_DISTANCE;
    });
    if (!tooClose) kept.push(hs);
  }
  return kept;
}

/** Pillar color mapping for globe hotspot markers. */
const PILLAR_MARKER_COLORS: Record<string, number> = {
  problems: 0xff4444,
  solutions: 0x44cc44,
  projects: 0x4df0ff,
  enablers: 0x3399ff,
  communities: 0xcccccc,
};

const DEFAULT_MARKER_COLOR = 0x4df0ff; // cyan

/** Off-white for community hotspots — distinct from colored project markers. */
const COMMUNITY_MARKER_COLOR = 0xe0e0e0;

interface ParsedProject {
  [key: string]: any;
  _lat: number;
  _lon: number;
  _createdAt: string;
}

/**
 * Extract up to `limit` hotspot markers from project data.
 *
 * 1. Filters to projects with valid lat/lon
 * 2. Sorts by createdAt descending (newest first)
 * 3. Takes first `limit` results
 * 4. Deduplicates by rounded lat/lon (~0.01 degree precision)
 * 5. Maps projectCategory to pillar color
 *
 * Expects projects in the "seed shape" used throughout the app
 * (project_GUID, project_name, location_lat, location_lon, etc.)
 */
export function extractHotspots(
  projects: Record<string, any>[],
  limit: number = 20
): HotspotData[] {
  // Filter to projects with valid GPS coordinates and enrich with parsed coords
  const withCoords: ParsedProject[] = [];

  for (const p of projects) {
    const lat = parseFloat(p.location_lat || p.locationLat || '');
    const lon = parseFloat(p.location_lon || p.locationLon || '');
    if (isNaN(lat) || isNaN(lon)) continue;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) continue;
    withCoords.push({
      ...p,
      _lat: lat,
      _lon: lon,
      _createdAt: p.createdAt || p.created_at || '',
    });
  }

  // Sort by creation date descending (newest first)
  withCoords.sort((a, b) => {
    if (!a._createdAt && !b._createdAt) return 0;
    if (!a._createdAt) return 1;
    if (!b._createdAt) return -1;
    return b._createdAt.localeCompare(a._createdAt);
  });

  // Take first N
  const top = withCoords.slice(0, limit);

  // Deduplicate by rounded coordinates (~0.01 degree ≈ ~1km precision)
  const seen = new Set<string>();
  const deduped = top.filter((p) => {
    const key = `${Math.round(p._lat * 100)},${Math.round(p._lon * 100)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Map to hotspot data
  return deduped.map((p) => {
    const category = (
      p.project_category ||
      p.projectCategory ||
      ''
    ).toLowerCase();
    const color = PILLAR_MARKER_COLORS[category] ?? DEFAULT_MARKER_COLOR;

    return {
      id: p.project_GUID || p.id || '',
      name: p.project_name || p.name || '',
      lat: p._lat,
      lon: p._lon,
      color,
      entityKind: "project" as const,
    };
  });
}

/**
 * Extract hotspot markers from community entities.
 *
 * Only active communities with resolvable coordinates appear on the globe.
 * Community hotspots use a fixed off-white color (no RGB cycling).
 */
export function extractCommunityHotspots(
  communities: Record<string, any>[],
  limit: number = 30,
  relationships?: Record<string, any>[],
): HotspotData[] {
  const results: HotspotData[] = [];

  for (const c of communities) {
    // DEV-1483 — a workshop is never a hotspot. Categorical, and checked FIRST:
    // this is a discoverability rule, not a permission one, so it must not be
    // satisfiable by being active or public. (In practice a workshop rarely has
    // coordinates to plot, so this was latent rather than live — but "it doesn't
    // happen to leak today" is not a filter.)
    if (isWorkshop(c)) continue;

    // Only show active, public communities on the globe
    const isActive = c.is_active !== false && c.isActive !== false;
    const isVisible = c.visibility !== "private";
    if (!isActive || !isVisible) continue;

    const coords = resolveEntityCoords(c, communities, relationships);
    if (!coords) continue;

    results.push({
      id: c.entity_guid || c.entityGuid || c.id || "",
      name: c.name || c.entity_name || "(unnamed)",
      lat: coords.lat,
      lon: coords.lon,
      color: COMMUNITY_MARKER_COLOR,
      entityKind: "community",
    });
  }

  return results.slice(0, limit);
}
